import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Phone, 
  MapPin, 
  Store,
  Search,
  Loader2,
  Users,
  CreditCard,
  FileText,
  Wallet,
  Download,
  Globe
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CustomersPage = () => {
  const { API_URL, getAuthHeader } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const isRTL = language === 'ar' || language === 'ku';
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    shop_name: '',
    phone: '',
    whatsapp_phone: '',
    address: '',
    area: '',
    delivery_order: 0,
    prices: [],
    tax_rate: 0,
    pricing_type: 'box',  // 'box' = بالصندوق, 'piece' = بالقطعة
    latitude: '',
    longitude: '',
    portal_username: '',
    portal_password: '',
    preferred_language: 'ar',  // ar, de, tr
    whatsapp_group_link: ''  // رابط مجموعة الواتساب
  });
  const [paymentData, setPaymentData] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    payment_type: 'full',
    notes: ''
  });
  const [openingBalance, setOpeningBalance] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/api/customers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/products`, { headers: getAuthHeader() })
      ]);
      setCustomers(customersRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('loadError') || 'حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setSelectedCustomer(null);
    setFormData({
      name: '',
      shop_name: '',
      phone: '',
      address: '',
      area: '',
      delivery_order: customers.length + 1,
      prices: products.map(p => ({ product_id: p.id, price: p.default_price })),
      tax_rate: 0,
      pricing_type: 'box',
      latitude: '',
      longitude: '',
      portal_username: '',
      portal_password: '',
      preferred_language: 'ar',
      whatsapp_group_link: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (customer) => {
    setSelectedCustomer(customer);
    // Merge existing prices with all products
    const prices = products.map(p => {
      const existing = customer.prices.find(cp => cp.product_id === p.id);
      return { product_id: p.id, price: existing ? existing.price : p.default_price };
    });
    setFormData({
      name: customer.name,
      shop_name: customer.shop_name,
      phone: customer.phone,
      address: customer.address,
      area: customer.area,
      delivery_order: customer.delivery_order,
      prices: prices,
      tax_rate: customer.tax_rate,
      pricing_type: customer.pricing_type || 'box',
      latitude: customer.latitude || '',
      longitude: customer.longitude || '',
      portal_username: customer.portal_username || '',
      portal_password: '',  // Don't show existing password
      preferred_language: customer.preferred_language || 'ar',
      whatsapp_group_link: customer.whatsapp_group_link || ''
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (customer) => {
    setSelectedCustomer(customer);
    setDeleteDialogOpen(true);
  };

  const openPaymentDialog = (customer) => {
    setSelectedCustomer(customer);
    setPaymentData({
      amount: customer.balance > 0 ? customer.balance.toString() : '',
      date: new Date().toISOString().split('T')[0],
      payment_type: 'full',
      notes: ''
    });
    setPaymentDialogOpen(true);
  };

  const openBalanceDialog = (customer) => {
    setSelectedCustomer(customer);
    setOpeningBalance(customer.opening_balance || 0);
    setBalanceDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    // Prepare data with proper coordinate parsing
    const submitData = {
      ...formData,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
    };

    try {
      if (selectedCustomer) {
        await axios.put(
          `${API_URL}/api/customers/${selectedCustomer.id}`,
          submitData,
          { headers: getAuthHeader() }
        );
        toast.success(t('customerUpdated') || 'تم تحديث الزبون بنجاح');
      } else {
        await axios.post(
          `${API_URL}/api/customers`,
          submitData,
          { headers: getAuthHeader() }
        );
        toast.success(t('customerAdded') || 'تم إضافة الزبون بنجاح');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving customer:', error);
      toast.error(t('saveError') || 'حدث خطأ في حفظ البيانات');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${API_URL}/api/customers/${selectedCustomer.id}`,
        { headers: getAuthHeader() }
      );
      toast.success(t('customerDeleted') || 'تم حذف الزبون بنجاح');
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error(t('deleteError') || 'حدث خطأ في حذف الزبون');
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      await axios.post(
        `${API_URL}/api/payments`,
        {
          customer_id: selectedCustomer.id,
          amount: parseFloat(paymentData.amount),
          date: paymentData.date,
          payment_type: paymentData.payment_type,
          notes: paymentData.notes
        },
        { headers: getAuthHeader() }
      );
      toast.success(t('paymentRecorded') || 'تم تسجيل الدفعة بنجاح');
      setPaymentDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving payment:', error);
      toast.error(t('paymentError') || 'حدث خطأ في حفظ الدفعة');
    } finally {
      setFormLoading(false);
    }
  };

  const handleOpeningBalance = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      await axios.put(
        `${API_URL}/api/customers/${selectedCustomer.id}/opening-balance`,
        { opening_balance: parseFloat(openingBalance) || 0 },
        { headers: getAuthHeader() }
      );
      toast.success(t('openingBalanceUpdated') || 'تم تحديث الرصيد الافتتاحي بنجاح');
      setBalanceDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error updating opening balance:', error);
      toast.error(t('openingBalanceError') || 'حدث خطأ في تحديث الرصيد');
    } finally {
      setFormLoading(false);
    }
  };

  const updatePrice = (productId, price) => {
    setFormData({
      ...formData,
      prices: formData.prices.map(p => 
        p.product_id === productId ? { ...p, price: parseFloat(price) || 0 } : p
      )
    });
  };

  const filteredCustomers = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.shop_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.area.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatCurrency = (amount) => {
    return amount.toFixed(2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div data-testid="customers-page" className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-stone-900">{t('customers')}</h1>
          <p className="text-stone-500 mt-1">{customers.length} {t('customerCount')}</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate('/export-statements')}
            variant="outline"
            className="border-blue-300 text-blue-600 hover:bg-blue-50"
          >
            <Download className="w-5 h-5 me-2" />
            تصدير جميع الحسابات
          </Button>
          <Button
            data-testid="add-customer-btn"
            onClick={openAddDialog}
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
          >
            <Plus className="w-5 h-5 me-2" />
            {t('addCustomer')}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
        <Input
          data-testid="search-customers-input"
          type="text"
          placeholder={t('searchCustomers')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="ps-10 h-11"
        />
      </div>

      {/* Customers List */}
      {filteredCustomers.length === 0 ? (
        <div className="empty-state">
          <Users className="empty-state-icon" />
          <h3 className="empty-state-title">{t('noCustomers')}</h3>
          <p className="empty-state-description">
            {searchQuery ? t('noSearchResults') : t('addFirstCustomer')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredCustomers.map((customer, index) => (
            <Card key={customer.id} className="border-stone-200 hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Customer Number & Info */}
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center font-bold text-amber-700">
                      {customer.delivery_order}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-stone-900">{customer.name}</h3>
                      {customer.shop_name && (
                        <p className="text-sm text-stone-500 flex items-center gap-1 mt-0.5">
                          <Store className="w-3 h-3" />
                          {customer.shop_name}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-3 mt-2 text-sm text-stone-500">
                        {customer.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {customer.phone}
                          </span>
                        )}
                        {customer.area && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {customer.area}
                          </span>
                        )}
                        {customer.whatsapp_group_link && (
                          <a 
                            href={customer.whatsapp_group_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-green-600 hover:text-green-700"
                          >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                            مجموعة
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Balance Info */}
                  <div className="flex items-center gap-4">
                    <div className="text-center px-4 py-2 bg-stone-50 rounded-lg">
                      <p className="text-xs text-stone-500">{t('totalDebt')}</p>
                      <p className="font-bold text-stone-900">{formatCurrency(customer.total_debt)} €</p>
                      {customer.opening_balance > 0 && (
                        <p className="text-[10px] text-orange-600">(دين سابق: €{customer.opening_balance.toFixed(2)})</p>
                      )}
                    </div>
                    <div className="text-center px-4 py-2 bg-emerald-50 rounded-lg">
                      <p className="text-xs text-emerald-600">{t('totalPaid')}</p>
                      <p className="font-bold text-emerald-700">{formatCurrency(customer.total_paid)} €</p>
                    </div>
                    <div className={`text-center px-4 py-2 rounded-lg ${customer.balance > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                      <p className={`text-xs ${customer.balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{t('balance')}</p>
                      <p className={`font-bold ${customer.balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                        {formatCurrency(customer.balance)} €
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openPaymentDialog(customer)}
                      className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                      title={t('addPayment')}
                    >
                      <CreditCard className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openBalanceDialog(customer)}
                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                      title="تعديل الدين السابق"
                    >
                      <Wallet className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate(`/customer-statement/${customer.id}`)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title={t('customerStatement')}
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`edit-customer-${customer.id}`}
                      onClick={() => openEditDialog(customer)}
                      className="text-stone-500 hover:text-amber-600"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`delete-customer-${customer.id}`}
                      onClick={() => openDeleteDialog(customer)}
                      className="text-stone-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Customer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedCustomer ? t('editCustomer') : t('addCustomer')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('customerName')} *</Label>
                <Input
                  data-testid="customer-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>{t('shopName')}</Label>
                <Input
                  data-testid="customer-shop-input"
                  value={formData.shop_name}
                  onChange={(e) => setFormData({ ...formData, shop_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('phone')}</Label>
                <Input
                  data-testid="customer-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  {t('whatsappPhone') || 'رقم الواتساب'}
                </Label>
                <Input
                  data-testid="customer-whatsapp-input"
                  value={formData.whatsapp_phone}
                  onChange={(e) => setFormData({ ...formData, whatsapp_phone: e.target.value })}
                  placeholder="+49123456789"
                  dir="ltr"
                  className="text-left"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('area')}</Label>
                <Input
                  data-testid="customer-area-input"
                  value={formData.area}
                  onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>{t('address')}</Label>
                <Input
                  data-testid="customer-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('deliveryOrder')}</Label>
                <Input
                  data-testid="customer-delivery-order-input"
                  type="number"
                  min="1"
                  value={formData.delivery_order}
                  onChange={(e) => setFormData({ ...formData, delivery_order: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('taxRate')} %</Label>
                <Select
                  value={formData.tax_rate.toString()}
                  onValueChange={(value) => setFormData({ ...formData, tax_rate: parseFloat(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0%</SelectItem>
                    <SelectItem value="7">7%</SelectItem>
                    <SelectItem value="19">19%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('pricingType')}</Label>
                <Select
                  value={formData.pricing_type}
                  onValueChange={(value) => setFormData({ ...formData, pricing_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="box">{t('byBox')}</SelectItem>
                    <SelectItem value="piece">{t('byPiece')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Location Coordinates */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  خط العرض (Latitude)
                </Label>
                <Input
                  data-testid="customer-latitude-input"
                  type="text"
                  inputMode="decimal"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  placeholder="مثال: 52.520008"
                  dir="ltr"
                  className="text-left"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-green-600" />
                  خط الطول (Longitude)
                </Label>
                <Input
                  data-testid="customer-longitude-input"
                  type="text"
                  inputMode="decimal"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  placeholder="مثال: 13.404954"
                  dir="ltr"
                  className="text-left"
                />
              </div>
              
              {/* Preferred Language for Delivery Messages */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-purple-600" />
                  لغة رسائل التسليم
                </Label>
                <Select
                  value={formData.preferred_language}
                  onValueChange={(value) => setFormData({ ...formData, preferred_language: value })}
                >
                  <SelectTrigger data-testid="customer-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ar">🇸🇦 العربية</SelectItem>
                    <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
                    <SelectItem value="tr">🇹🇷 Türkçe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* WhatsApp Group Link */}
              <div className="space-y-2 col-span-2">
                <Label className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  رابط مجموعة واتساب
                </Label>
                <Input
                  data-testid="customer-whatsapp-group-input"
                  type="url"
                  value={formData.whatsapp_group_link}
                  onChange={(e) => setFormData({ ...formData, whatsapp_group_link: e.target.value })}
                  placeholder="https://chat.whatsapp.com/..."
                  dir="ltr"
                  className="text-left"
                />
                <p className="text-xs text-stone-500">
                  رابط دعوة المجموعة لإرسال الرسائل الجماعية
                </p>
              </div>
            </div>

            {/* Product Prices */}
            <div className="border-t border-stone-200 pt-4">
              <h4 className="font-bold text-stone-900 mb-3">{t('customerPrices')} (€)</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {products.map((product) => {
                  const priceItem = formData.prices.find(p => p.product_id === product.id);
                  return (
                    <div key={product.id} className="space-y-1">
                      <Label className="text-sm">{product.name}</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        value={priceItem?.price ?? ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(',', '.');
                          updatePrice(product.id, val);
                        }}
                        className="h-9"
                        placeholder="0.00"
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Customer Portal Credentials */}
            <div className="border-t border-stone-200 pt-4">
              <h4 className="font-bold text-stone-900 mb-3">بوابة الزبون (اختياري)</h4>
              <p className="text-sm text-stone-500 mb-3">
                أنشئ بيانات دخول للزبون ليتمكن من إنشاء طلباته عبر الرابط: /portal
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>اسم المستخدم</Label>
                  <Input
                    data-testid="portal-username-input"
                    value={formData.portal_username}
                    onChange={(e) => setFormData({ ...formData, portal_username: e.target.value })}
                    placeholder="مثال: customer1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{selectedCustomer ? 'كلمة مرور جديدة' : 'كلمة المرور'}</Label>
                  <Input
                    data-testid="portal-password-input"
                    type="password"
                    value={formData.portal_password}
                    onChange={(e) => setFormData({ ...formData, portal_password: e.target.value })}
                    placeholder={selectedCustomer ? 'اتركه فارغاً للإبقاء على القديمة' : 'كلمة المرور'}
                  />
                </div>
              </div>
              {selectedCustomer?.has_portal_access && (
                <p className="text-sm text-emerald-600 mt-2">✓ لدى هذا الزبون صلاحية الدخول للبوابة</p>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                data-testid="save-customer-btn"
                className="bg-amber-600 hover:bg-amber-700"
                disabled={formLoading}
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : selectedCustomer ? t('save') : t('add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('addPayment')} - {selectedCustomer?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePayment} className="space-y-4">
            <div className="p-3 bg-stone-50 rounded-lg text-center">
              <p className="text-sm text-stone-500">{t('balance')}</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(selectedCustomer?.balance || 0)} €</p>
            </div>

            <div className="space-y-2">
              <Label>{t('amount')} (€)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>{t('date')}</Label>
              <Input
                type="date"
                value={paymentData.date}
                onChange={(e) => setPaymentData({ ...paymentData, date: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>{t('paymentType')}</Label>
              <Select
                value={paymentData.payment_type}
                onValueChange={(value) => setPaymentData({ ...paymentData, payment_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">{t('fullPayment')}</SelectItem>
                  <SelectItem value="partial">{t('partialPayment')}</SelectItem>
                  <SelectItem value="deferred">{t('credit')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('notes')}</Label>
              <Input
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                placeholder={t('optionalNotes')}
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={formLoading}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('addPayment')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteCustomerConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteCustomerWarning')} "{selectedCustomer?.name}"
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Opening Balance Dialog */}
      <Dialog open={balanceDialogOpen} onOpenChange={setBalanceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-orange-600" />
              تعديل الدين السابق
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleOpeningBalance} className="space-y-4">
            <div className="p-4 bg-stone-50 rounded-lg">
              <p className="text-stone-600 font-medium">{selectedCustomer?.name}</p>
              <div className="flex justify-between mt-2 text-sm">
                <span className="text-stone-500">الرصيد الحالي:</span>
                <span className={`font-bold ${selectedCustomer?.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  €{selectedCustomer?.balance?.toFixed(2) || '0.00'}
                </span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="openingBalance">الدين السابق (€)</Label>
              <Input
                id="openingBalance"
                type="number"
                min="0"
                step="0.01"
                value={openingBalance}
                onChange={(e) => setOpeningBalance(parseFloat(e.target.value) || 0)}
                className="text-xl font-bold text-center"
                placeholder="0.00"
              />
              <p className="text-xs text-stone-500">
                أدخل المبلغ المستحق على الزبون من قبل استخدام البرنامج (الدين القديم)
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setBalanceDialogOpen(false)}>
                إلغاء
              </Button>
              <Button type="submit" className="bg-orange-600 hover:bg-orange-700" disabled={formLoading}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'حفظ'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
